import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { CompanyType } from "@entrilia/shared/api/clientTypes";
import { allCompanyTypes } from "@entrilia/shared/api/clientTypes";
import type { JobProgress } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import { trimChars } from "@entrilia/shared/utilities/stringHelper";
import { api } from "../../../api/operationsApi";
import type { ClientCodeFromWebsiteUrlAccessors } from "../../../hooks/useClientCodeFromWebsiteUrl";
import { useClientCodeFromWebsiteUrl } from "../../../hooks/useClientCodeFromWebsiteUrl";
import { useExecutionState } from "../../../hooks/useExecutionState";
import { getVariables } from "../../../variables";
import { isFormValid } from "../../../utilities/forms";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import OrganizationSelector from "../../common/OrganizationSelector";
import PageTitle from "../../common/PageTitle";
import { detectCompanyTypeFromName, doesClientCodeExist, getClientCodeHelperText } from "./clientManagementFormHelpers";
import type { FundManagerProvisioningForm } from "./fundManagerProvisioningForm";
import {
  getInitialFundManagerProvisioningForm,
  mapFundManagerProvisioningFormToRequest,
  validateFundManagerProvisioningForm,
} from "./fundManagerProvisioningForm";

const provisionFundManagerClient = withErrorHandling(api.clientProvisioning.provisionFundManagerClient);
const cancelProvisionJob = withErrorHandling(api.clientProvisioning.cancelJob);

const clientCodeAccessors: ClientCodeFromWebsiteUrlAccessors<FundManagerProvisioningForm> = {
  getClientCode: (form) => form.clientCode,
  getWebsiteUrl: (form) => form.websiteUrl,
  setClientCode: (form, clientCode) => ({ ...form, clientCode }),
};

const FundManagerProvisioning = () => {
  const [form, setForm] = useState<FundManagerProvisioningForm>(getInitialFundManagerProvisioningForm);
  const [clientCodeExists, setClientCodeExists] = useState(false);
  const [showAllBcCompanies, setShowAllBcCompanies] = useState(false);
  const [operationId, setOperationId] = useState<string>();
  const [isCancelling, setIsCancelling] = useState(false);
  const { executionState, setExecutionState } = useExecutionState();

  const prefillClientCodeFromUrl = useClientCodeFromWebsiteUrl(form, setForm, clientCodeAccessors);

  const [organizations, fetchOrganizationsError, { fetch: refetchOrganizations }] = useFetch(
    api.clientProvisioning.getExistingOrganizations
  );

  const handleJobFinished = useCallback(
    (jobProgress: JobProgress) => {
      setOperationId(undefined);
      setIsCancelling(false);

      if (jobProgress.properties["Cancelled"] === "true") {
        setExecutionState({
          status: "warning",
          text: "Provisioning was cancelled.",
          details: ["The client may be partially created. Deprovision it before retrying."],
        });
        refetchOrganizations();
        return;
      }

      if (jobProgress.isSuccess) {
        const clientCode = jobProgress.properties["CreatedClientCode"];
        setExecutionState({
          status: "success",
          details: ["Client provisioning completed successfully."],
          links: clientCode
            ? [{ label: "Open in Admin Portal", href: `${getVariables().ADMIN_PORTAL_URI}/${clientCode}/home` }]
            : undefined,
        });
        refetchOrganizations();
      } else {
        setExecutionState({
          status: "error",
          errorMessage: jobProgress.errorMessage,
        });
      }
    },
    [setExecutionState, refetchOrganizations]
  );

  const { jobProgress, startJobProgressCheck } = useJobProgress(api.clientProvisioning.getJobProgress, {
    onJobFinished: handleJobFinished,
  });

  const getBcCompanies = useCallback(() => {
    if (!form.organization) {
      return Promise.resolve(createApiResponse(undefined));
    }

    return api.clientProvisioning.getExistingBcCompanies(form.organization.clientCode, form.bcTenant ?? undefined);
  }, [form.organization, form.bcTenant]);

  const [bcTenantCompanies, fetchBcCompaniesError, { isFetching: isFetchingBcCompanies }] = useFetch(getBcCompanies);

  const filteredBcCompanies = useMemo(() => {
    if (!bcTenantCompanies?.companies || !form.organization || showAllBcCompanies) {
      return bcTenantCompanies?.companies || [];
    }

    // Get set of already provisioned BC company IDs
    const provisionedBcCompanyIds = new Set(form.organization.managesClients.map((client) => client.bcCompanyId));

    // Filter out companies that are already provisioned
    return bcTenantCompanies.companies.filter((company) => !provisionedBcCompanyIds.has(company.companyId));
  }, [bcTenantCompanies, form.organization, showAllBcCompanies]);

  if (fetchOrganizationsError) {
    return <LoadingFailedAlert apiError={fetchOrganizationsError} />;
  }

  if (organizations === undefined) {
    return <InlineLoader />;
  }

  const loadedBcTenant =
    bcTenantCompanies?.tenantId && bcTenantCompanies.environment
      ? { tenantId: bcTenantCompanies.tenantId, environment: bcTenantCompanies.environment }
      : undefined;

  // The loaded companies lag a tenant switch by a render, so only trust a response matching the chosen tenant.
  const selectedBcTenant =
    loadedBcTenant &&
    (!form.bcTenant ||
      (form.bcTenant.tenantId === loadedBcTenant.tenantId && form.bcTenant.environment === loadedBcTenant.environment))
      ? loadedBcTenant
      : undefined;

  const handleProvision = async () => {
    if (!selectedBcTenant) {
      return;
    }

    const validationResults = validateFundManagerProvisioningForm(form);
    setForm((prev) => ({ ...prev, validationResults }));
    if (!isFormValid(validationResults)) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const payload = mapFundManagerProvisioningFormToRequest(form, selectedBcTenant);
    const [resp, error] = await provisionFundManagerClient(payload);
    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    setOperationId(resp.operationId);
    startJobProgressCheck(resp.operationId);
  };

  const handleCancel = async () => {
    if (!operationId) {
      return;
    }

    setIsCancelling(true);
    const [, error] = await cancelProvisionJob(operationId);
    if (error) {
      logError(error, "[FundManagerProvisioning] cancelJob");
      setExecutionState({ status: "error", error });
      setIsCancelling(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Provision Fund Manager Client" subtitle="📋 What gets provisioned" defaultExpanded={false}>
        💼 Create a new Fund Manager (portfolio company) under an existing Fund Administrator organization
        <br />
        <br />
        • Client record linked to parent organization
        <br />
        • Business Central company connection
        <br />
        • Data Warehouse (Snowflake) schema and sync
        <br />
        • Optional features: Investor Relations, Expense Management, Portfolio Monitoring
        <br />
        • Azure Blob Storage containers
        <br />
        • API Gateway, Backup Master, and Passthrough applications
        <br />
        • Financial integration applications
        <br />
        • Agent Inbox and video library
        <br />
        <br />
        <strong>Key differences from Fund Admin:</strong>
        <br />
        ✓ Managed by a parent Fund Administrator organization
        <br />
        ✓ Connects to existing BC tenant (company within organization&apos;s BC)
        <br />
        ✓ Full DWH sync runs automatically during provisioning
        <br />
        ✓ Inherits location (US/EU) from parent organization
        <br />
        <br />
        ⏱️ <strong>Duration:</strong> Full provisioning process takes several minutes up to an hour depending on volume
        of existing data in BC for the new client. If the new company is a copy of a large client, expect longer times.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <OrganizationSelector
          organizations={organizations}
          value={form.organization}
          onChange={(value) => setForm({ ...getInitialFundManagerProvisioningForm(), organization: value })}
          validationResult={form.validationResults.organization}
        />
        <FieldHelp>
          Parent Fund Administrator organization that will manage this Fund Manager.
          <br />
          <br />
          The new client inherits location, BC tenant, and is added to the organization&apos;s managed clients.
        </FieldHelp>
      </Stack>
      {form.organization && bcTenantCompanies && bcTenantCompanies.availableTenants.length > 1 && (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <Autocomplete
            fullWidth
            options={bcTenantCompanies.availableTenants}
            value={form.bcTenant}
            onChange={(_, value) => setForm((prev) => ({ ...prev, bcTenant: value, bcCompany: null }))}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(option, value) =>
              option.tenantId === value.tenantId && option.environment === value.environment
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="BC Tenant"
                helperText={form.bcTenant ? "" : "Select a BC tenant to list its companies"}
              />
            )}
          />
          <FieldHelp>
            This organization&apos;s clients span several Business Central tenants and environments.
            <br />
            <br />
            Pick the tenant holding the company to connect to this Fund Manager.
          </FieldHelp>
        </Stack>
      )}
      {form.organization && (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <Autocomplete
            fullWidth
            disabled={!selectedBcTenant || isFetchingBcCompanies}
            options={filteredBcCompanies}
            value={form.bcCompany}
            onChange={(_, value) => {
              const cleanedTitle = value ? trimChars(value.displayName, "[", "]") : "";
              const companyType = value ? detectCompanyTypeFromName(value.displayName) : "Production";
              setForm((prev) => ({ ...prev, bcCompany: value, title: cleanedTitle, companyType }));
            }}
            getOptionLabel={(option) => `${option.displayName}`}
            isOptionEqualToValue={(option, value) => option.companyId === value.companyId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="BC Company"
                error={form.validationResults.bcCompany?.isValid === false}
                helperText={form.validationResults.bcCompany?.error}
              />
            )}
          />
          <FieldHelp>
            Business Central company from the organization&apos;s BC tenant to connect to this Fund Manager.
            <br />
            <br />
            This links the Fund Manager to a specific accounting company in BC. DWH will sync data from this company.
          </FieldHelp>
        </Stack>
      )}
      {form.organization && selectedBcTenant && (
        <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
          <FormControlLabel
            control={
              <Checkbox checked={showAllBcCompanies} onChange={(_, checked) => setShowAllBcCompanies(checked)} />
            }
            label="Show all companies (including already provisioned)"
          />
          <FieldHelp>
            By default, only companies that haven&apos;t been provisioned yet are shown.
            <br />
            <br />
            Enable this to see all Business Central companies, including those that are already provisioned as Fund
            Managers.
          </FieldHelp>
        </Stack>
      )}
      {fetchBcCompaniesError && <LoadingFailedAlert apiError={fetchBcCompaniesError} />}
      {form.organization && (
        <Stack spacing={3} sx={{ alignItems: "flex-start", width: "100%" }}>
          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="Title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              error={form.validationResults.title?.isValid === false}
              helperText={form.validationResults.title?.error}
            />
            <FieldHelp>
              Display name of the Fund Manager.
              <br />
              Use BC company name for consistency.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="Website URL"
              value={form.websiteUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, websiteUrl: e.target.value }))}
              onBlur={prefillClientCodeFromUrl}
              error={form.validationResults.websiteUrl?.isValid === false}
              helperText={form.validationResults.websiteUrl?.error}
            />
            <FieldHelp>
              Public website URL of the Fund Manager.
              <br />
              <br />
              The website&apos;s favicon will be automatically extracted and used as the client&apos;s icon throughout
              the system.
              <br />
              <br />
              Optional, but recommended for better visual identification.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <TextField
              fullWidth
              label="Code"
              value={form.clientCode}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, clientCode: e.target.value }));
                setClientCodeExists(false);
              }}
              onBlur={() => {
                if (form.clientCode.trim() && organizations) {
                  setClientCodeExists(doesClientCodeExist(form.clientCode, organizations));
                }
              }}
              error={form.validationResults.clientCode?.isValid === false}
              helperText={getClientCodeHelperText(
                form.validationResults.clientCode?.error,
                clientCodeExists,
                form.continueIfClientAlreadyExists
              )}
              sx={
                clientCodeExists
                  ? {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "warning.main",
                          borderWidth: 2,
                        },
                      },
                    }
                  : undefined
              }
            />
            <FieldHelp>
              <strong>Naming Convention:</strong> Use company&apos;s domain name without extension
              <br />
              Example: &quot;Super Funds, Inc.&quot; with domain &quot;superfunds.com&quot; →{" "}
              <strong>superfunds</strong>
              <br />
              <br />
              <strong>Validation Rules:</strong>
              <br />
              • 3-30 characters
              <br />
              • Only lowercase letters and digits
              <br />
              • No special characters (no dots, dashes, underscores)
              <br />• Must be unique across all clients
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControl fullWidth>
              <InputLabel>Company Type</InputLabel>
              <Select
                label="Company Type"
                value={form.companyType}
                onChange={(e) => setForm((prev) => ({ ...prev, companyType: e.target.value as CompanyType }))}
              >
                {allCompanyTypes.map((companyType) => (
                  <MenuItem key={companyType} value={companyType}>
                    {companyType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FieldHelp>
              <strong>Production:</strong> Live company with real data
              <br />
              <strong>Demo:</strong> Demonstration environment
              <br />
              <strong>Template:</strong> Template company for creating new companies
              <br />
              <strong>Training:</strong> Training/educational environment
              <br />
              <strong>Sandbox:</strong> Testing/development environment
              <br />
              <br />
              Determines how the company is treated in reports and operations.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControl fullWidth>
              <FormLabel component="legend">Enabled Features</FormLabel>
              <FormGroup>
                <FormControlLabel
                  label="Investor Portal"
                  control={
                    <Checkbox
                      checked={form.enableInvestorPortal}
                      onChange={(_, checked) =>
                        setForm((prev) => ({
                          ...prev,
                          enableInvestorPortal: checked,
                        }))
                      }
                    />
                  }
                />
              </FormGroup>
              <FormGroup>
                <FormControlLabel
                  label="Expense Management"
                  control={
                    <Checkbox
                      checked={form.enableExpenseManagement}
                      onChange={(_, checked) =>
                        setForm((prev) => ({
                          ...prev,
                          enableExpenseManagement: checked,
                        }))
                      }
                    />
                  }
                />
              </FormGroup>
              <FormGroup>
                <FormControlLabel
                  label="Portfolio Monitoring Data Collection"
                  control={
                    <Checkbox
                      checked={form.enablePortfolioMonitoringDataCollection}
                      onChange={(_, checked) =>
                        setForm((prev) => ({ ...prev, enablePortfolioMonitoringDataCollection: checked }))
                      }
                    />
                  }
                />
              </FormGroup>
            </FormControl>
            <FieldHelp>
              <strong>Investor Portal:</strong> Enable the investor portal and Fundraising
              <br />
              <strong>Expense Management:</strong> Enable expense tracking and approval workflows
              <br />
              <strong>Portfolio Monitoring:</strong> Enable data collection for portfolio performance tracking
              <br />
              <br />
              Features can be enabled/disabled based on client needs.
            </FieldHelp>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
            <FormControl fullWidth>
              <FormControlLabel
                label="Continue if already exists"
                control={
                  <Checkbox
                    checked={form.continueIfClientAlreadyExists}
                    onChange={(_, checked) =>
                      setForm((prev) => ({
                        ...prev,
                        continueIfClientAlreadyExists: checked,
                      }))
                    }
                  />
                }
              />
            </FormControl>
            <FieldHelp>
              <strong>⚠️ IMPORTANT:</strong> Always start with this option <strong>DISABLED</strong> to prevent
              overwriting existing clients.
              <br />
              <br />
              <strong>Only enable if:</strong>
              <br />
              • Initial provisioning attempt failed
              <br />• You are re-provisioning after fixing an issue
            </FieldHelp>
          </Stack>

          <Divider flexItem />

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProvision}
              loading={executionState.status === "in-progress"}
              disabled={!selectedBcTenant || isFetchingBcCompanies}
            >
              Provision
            </Button>
            <Button
              variant="text"
              color="secondary"
              onClick={() =>
                setForm((prev) => ({
                  ...getInitialFundManagerProvisioningForm(),
                  organization: prev.organization,
                  bcTenant: prev.bcTenant,
                }))
              }
              disabled={executionState.status === "in-progress"}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      )}
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
      {jobProgress && (
        <AsyncJobProgress
          title="Please wait..."
          jobProgress={jobProgress}
          isCancelling={isCancelling}
          onCancel={handleCancel}
        />
      )}
    </Stack>
  );
};

export default FundManagerProvisioning;
